/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Formula } from '@gh/builder-shared-data';
import {
  combineReducers,
  composeSetters,
  fieldValidationSetter,
  onChangeReducer,
  removeDirtyRow,
  setDirty,
  setEntityField,
  setNewDirtyType,
  setRecalculationNeeded,
  setUpdatedDirtyType,
  Table,
  toOnChangeReducer,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { flatMap, isEqual, isNil, uniq } from 'lodash';
import {
  generateHpeComponentItemId,
  getHpeComponentCostCenterIds,
  getHpeComponentTopIds,
  HpeComponent,
  hpeFormulaNeedPropagationSetter,
  hpeQuantityResetSetter,
  isHpeComponentFieldAffectsPrice,
  validateHpeComponentField,
} from '../../../resources/hpe-details.model';
import {
  hpeComponentTable,
  HpeDetailsState,
  HpeDetailsStateEvent,
  HpeDetailsStateEventType,
  hpeItemTable,
  queryHpeComponent,
} from '../hpe-details.state';
import { addOrUpdateCostCenters, markCostCentersAsDirty } from './hpe-details-cc.reducers';
import { cuttingListReducer, rootCuttingListReducer } from './hpe-details-cutting-list.reducers';
import { selectionConsistencyReducer } from './hpe-details-selection.reducers';
import { addOrUpdateTops, getModifiedTops } from './hpe-details-top.reducers';

const setComponentField = composeSetters(
  setEntityField,
  hpeFormulaNeedPropagationSetter,
  hpeQuantityResetSetter,
  setUpdatedDirtyType,
  fieldValidationSetter(validateHpeComponentField));

export const hpeDetailsComponentReducer = onChangeReducer(
  combineReducers({
    hpComponents: componentsReducer,
  }),
  componentItemsOnChangeReducer,
  costCenterComponentOnChangeReducer,
  formulaComponentOnChangeReducer,
  rootComponentOnChangeReducer,
  toOnChangeReducer(selectionConsistencyReducer),
);

function componentsReducer(state: Table<HpeComponent>, event: HpeDetailsStateEvent): Table<HpeComponent> {
  switch (event.type) {
    case HpeDetailsStateEventType.ComponentAddSet:
      const { components } = event.componentSet;
      return hpeComponentTable.insertAll(state, components.map(setNewDirtyType));
    case HpeDetailsStateEventType.ComponentSetField:
      return hpeComponentTable.mergeById(
        state,
        event.componentId,
        (row: HpeComponent) => setComponentField(row, event.name, event.value));
    case HpeDetailsStateEventType.CuttingListItemAdd:
    case HpeDetailsStateEventType.CuttingListItemRemove:
    case HpeDetailsStateEventType.CuttingListItemSetField:
    case HpeDetailsStateEventType.ResetFormulaNeedPropagation:
      return hpeComponentTable.mergeById(
        state,
        event.sourceId,
        (row: HpeComponent) => componentReducer(row, event));
    case HpeDetailsStateEventType.ComponentRemove:
      return hpeComponentTable.mergeById(state, event.componentId, removeDirtyRow);
    default:
      return state;
  }
}

function componentReducer(component: HpeComponent, event: HpeDetailsStateEvent): HpeComponent {
  switch (event.type) {
    case HpeDetailsStateEventType.CuttingListItemAdd:
    case HpeDetailsStateEventType.CuttingListItemRemove:
    case HpeDetailsStateEventType.CuttingListItemSetField:
      return setComponentField(component, 'cuttingList', cuttingListReducer(component.cuttingList || [], event));
    case HpeDetailsStateEventType.ResetFormulaNeedPropagation:
      return setComponentField(component, 'isFormulaNeedPropagation', false);
    default:
      return component;
  }
}

function componentItemsOnChangeReducer(oldState: HpeDetailsState,
                                       newState: HpeDetailsState,
                                       event: HpeDetailsStateEvent): HpeDetailsState {
  switch (event.type) {
    case HpeDetailsStateEventType.ComponentAddSet:
      const { items } = event.componentSet;
      return setEntityField(newState, 'hpItems', hpeItemTable.insertAll(newState.hpItems, items));
    case HpeDetailsStateEventType.ComponentRemove:
      return setEntityField(
        newState,
        'hpItems',
        hpeItemTable.removeAll(oldState.hpItems, getComponentItemIds(oldState, event.componentId)));
    default:
      return newState;
  }
}

function costCenterComponentOnChangeReducer(oldState: HpeDetailsState,
                                            newState: HpeDetailsState,
                                            event: HpeDetailsStateEvent): HpeDetailsState {
  switch (event.type) {
    case HpeDetailsStateEventType.ComponentAddSet:
      return addOrUpdateCostCenters(newState, event.componentSet.affectedCostCenters);
    case HpeDetailsStateEventType.ComponentSetField:
      return markCostCentersAsDirty(newState, getComponentCostCenterIds(newState, event.componentId));
    case HpeDetailsStateEventType.ComponentRemove:
      return markCostCentersAsDirty(newState, getComponentCostCenterIds(oldState, event.componentId));
    case HpeDetailsStateEventType.CuttingListItemAdd:
    case HpeDetailsStateEventType.CuttingListItemSetField:
    case HpeDetailsStateEventType.CuttingListItemRemove:
      return markCostCentersAsDirty(newState, getComponentCostCenterIds(oldState, event.sourceId));
    default:
      return newState;
  }
}

function formulaComponentOnChangeReducer(oldState: HpeDetailsState,
                                         newState: HpeDetailsState,
                                         event: HpeDetailsStateEvent): HpeDetailsState {
  switch (event.type) {
    case HpeDetailsStateEventType.ComponentAddSet:
      return addOrUpdateTops(newState, uniq(flatMap(event.componentSet.components, getHpeComponentTopIds)));
    case HpeDetailsStateEventType.ComponentSetField:
      if (event.name === 'formula') {
        const formula: Formula = event.value;
        const component = <HpeComponent>hpeComponentTable.byId(newState.hpComponents, event.componentId);
        if (formula.valid && !isEqual(formula.takeOffParams, component.topIds)) {
          return addOrUpdateTops(
            setEntityField(
              newState,
              'hpComponents',
              hpeComponentTable.update(newState.hpComponents, { ...component, topIds: formula.takeOffParams })),
            getModifiedTops(getHpeComponentTopIds(component), formula.takeOffParams));
        }
      }
      return newState;
    case HpeDetailsStateEventType.ComponentRemove: {
      const component = <HpeComponent>hpeComponentTable.byId(oldState.hpComponents, event.componentId);
      return addOrUpdateTops(newState, getHpeComponentTopIds(component));
    }
    default:
      return newState;
  }
}

function rootComponentOnChangeReducer(_: HpeDetailsState,
                                      state: HpeDetailsState,
                                      event: HpeDetailsStateEvent): HpeDetailsState {
  switch (event.type) {
    case HpeDetailsStateEventType.ComponentAddSet:
    case HpeDetailsStateEventType.ComponentRemove:
      return setRecalculationNeeded(setDirty(state));
    case HpeDetailsStateEventType.ComponentSetField:
      if (isHpeComponentFieldAffectsPrice(event.name)) {
        return setRecalculationNeeded(setDirty(state));
      } else {
        return setDirty(state);
      }
    case HpeDetailsStateEventType.CuttingListItemAdd:
    case HpeDetailsStateEventType.CuttingListItemSetField:
    case HpeDetailsStateEventType.CuttingListItemRemove:
      return rootCuttingListReducer(state, event);
    default:
      return state;
  }
}

function getComponentItemIds(state: HpeDetailsState, componentId: ID): ID[] {
  const component = queryHpeComponent(state, componentId);

  if (isNil(component)) {
    return [];
  } else {
    return component.items.map((item) => generateHpeComponentItemId(componentId, item.costCenterId, item.itemId));
  }
}

function getComponentCostCenterIds(state: HpeDetailsState, componentId: ID): ID[] {
  const component = queryHpeComponent(state, componentId);

  if (isNil(component)) {
    return [];
  } else {
    return uniq([state.hpComponentsCcId].concat(getHpeComponentCostCenterIds(component)));
  }
}
